/**
 * Custom hook for accessibility features
 * Provides keyboard navigation, focus management, and ARIA support
 */

import { useEffect, useRef } from 'react';

interface UseAccessibilityOptions {
  autoFocus?: boolean;
  trapFocus?: boolean;
  escapeKey?: () => void;
  ariaLabel?: string;
}

export const useAccessibility = (options: UseAccessibilityOptions = {}) => {
  const {
    autoFocus = false,
    trapFocus = false,
    escapeKey,
    ariaLabel,
  } = options;

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Auto focus on mount
    if (autoFocus && containerRef.current) {
      setTimeout(() => {
        const firstFocusable = containerRef.current?.querySelector(
          'input:not([disabled]), button:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'
        ) as HTMLElement;
        
        if (firstFocusable) {
          firstFocusable.focus();
        }
      }, 100); // Small delay to ensure DOM is ready
    }
  }, [autoFocus]);

  useEffect(() => {
    if (!trapFocus) return;

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !containerRef.current) return;

      const focusableElements = containerRef.current.querySelectorAll(
        'input:not([disabled]), button:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'
      ) as NodeListOf<HTMLElement>;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTabKey);
    return () => document.removeEventListener('keydown', handleTabKey);
  }, [trapFocus]);

  useEffect(() => {
    if (!escapeKey) return;

    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        escapeKey();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [escapeKey]);

  // Announce to screen readers
  const announceToScreenReader = (message: string) => {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.style.position = 'absolute';
    announcement.style.left = '-10000px';
    announcement.style.width = '1px';
    announcement.style.height = '1px';
    announcement.style.overflow = 'hidden';
    announcement.textContent = message;
    
    document.body.appendChild(announcement);
    
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  };

  return {
    containerRef,
    announceToScreenReader,
  };
};